import { ApiService } from "../../../config";
import useUserStore from "../../../store/user";
import { AxiosRequestConfig } from "axios";
import axios from "axios";

export interface ProfileTrackingResponse {
  id: string;
  name: string;
  category: string;
  categoryId?: string;
  status: "active" | "pending" | "paused" | "preparing";
  avatar?: string;
  stats: {
    views: number;
    clicks: number;
    ranking: number;
  };
  // Campos adicionais
  shortName?: string;
  openingDate?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  description?: string;
  hours?: {
    monday?: string | string[];
    tuesday?: string | string[];
    wednesday?: string | string[];
    thursday?: string | string[];
    friday?: string | string[];
    saturday?: string | string[];
    sunday?: string | string[];
  };
  whatsapp?: string;
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
  };
  serviceAreas?: string[];
  latitude?: number;
  longitude?: number;
  specialHours?: Array<{ date: string; hours: string }>;
  amenities?: string[];
  accessibility?: string[];
  parking?: string[];
  payments?: string[];
  serviceOptions?: string[];
  secondaryCategories?: string[];
  secondaryCategoryIds?: string[];
  /** Link de avaliação do Google (writereview). Vem do /details e alimenta o
   *  Cartão de Avaliação da Presença Digital. */
  reviewUrl?: string;
  /** Fotos do perfil no Google. O /details já as devolve (BusinessProfileResponse
   *  .applyPhotos); é esta lista que alimenta a galeria e a sugestão de capa do
   *  mini-site. */
  photos?: string[];
}

// Serviços de Perfis em Acompanhamento
const workStatusByProfileStatus: Partial<
  Record<ProfileTrackingResponse["status"], string>
> = {
  active: "OPEN",
  paused: "CLOSED_TEMPORARILY",
};

const googleAttributeByLabel: Record<string, string> = {
  "Entrada com acessibilidade": "has_wheelchair_accessible_entrance",
  "Estacionamento acessível": "has_wheelchair_accessible_parking",
  "Banheiro acessível": "has_wheelchair_accessible_restroom",
  "Elevador acessível": "has_wheelchair_accessible_elevator",
  "Estacionamento gratuito": "has_free_parking",
  "Estacionamento pago": "has_paid_parking",
  "Estacionamento na rua": "has_street_parking",
  Valet: "has_valet_parking",
  "Cartões de crédito": "pay_credit_card",
  "Cartões de débito": "pay_debit_card",
  Dinheiro: "pay_cash_only",
  "Pagamento por NFC": "pay_mobile_nfc",
  Visa: "pay_visa",
  Mastercard: "pay_mastercard",
  "American Express": "pay_amex",
  "Atendimento presencial": "has_onsite_services",
  Entrega: "has_delivery",
  "Retirada na loja": "has_in_store_pickup",
  "Serviço online": "has_online_services",
  "Drive-through": "has_drive_through",
  "Wi-Fi gratuito": "has_wifi",
  "Ar-condicionado": "has_air_conditioning",
  "Pet-friendly": "welcomes_dogs",
  "Área externa": "has_outdoor_seating",
  Brinquedoteca: "has_childrens_play_area",
};

const labelByGoogleAttribute = Object.fromEntries(
  Object.entries(googleAttributeByLabel).map(([label, attribute]) => [
    attribute,
    label,
  ]),
);

const mapGoogleAttributeToLabel = (value: unknown) => {
  if (typeof value !== "string") return undefined;
  const attribute = value.split("/").pop() || value;
  return labelByGoogleAttribute[attribute] || value;
};

const dayKeys = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

type DayKey = (typeof dayKeys)[number];

const isOpenScheduleValue = (value: string | string[] | undefined) => {
  if (Array.isArray(value)) return value.length > 0;
  if (!value) return false;

  const normalized = value.trim().toLowerCase();
  return (
    normalized !== "" &&
    normalized !== "fechado" &&
    normalized !== "closed"
  );
};

const splitSpecialHourRange = (hours: string) => {
  const [startDate = "", endDate = ""] = hours
    .split("-")
    .map((value) => value.trim());

  return { startDate, endDate };
};

export const buildGeneralPayload = (data: Partial<ProfileTrackingResponse>) => {
  const workStatus = data.status ? workStatusByProfileStatus[data.status] : undefined;
  const payload: {
    name?: string;
    workStatus?: string;
    businessInauguration?: string;
  } = {};

  if (data.name !== undefined) payload.name = data.name;
  if (workStatus) payload.workStatus = workStatus;
  if (data.openingDate !== undefined) {
    payload.businessInauguration = data.openingDate;
  }

  return Object.keys(payload).length > 0 ? payload : null;
};

export const buildSchedulePayload = (
  data: Pick<Partial<ProfileTrackingResponse>, "hours" | "specialHours">,
) => {
  const defaultSchedule = dayKeys.reduce<Partial<Record<DayKey, boolean>>>(
    (acc, day) => {
      if (data.hours?.[day] !== undefined) {
        acc[day] = isOpenScheduleValue(data.hours[day]);
      }
      return acc;
    },
    {},
  );

  const extraSchedule = (data.specialHours || [])
    .filter((specialHour) => specialHour.date)
    .map((specialHour) => ({
      date: specialHour.date,
      ...splitSpecialHourRange(specialHour.hours),
    }));

  const payload: {
    defaultSchedule?: Partial<Record<DayKey, boolean>>;
    extraSchedule?: Array<{ date: string; startDate: string; endDate: string }>;
  } = {};

  if (Object.keys(defaultSchedule).length > 0) {
    payload.defaultSchedule = defaultSchedule;
  }
  if (extraSchedule.length > 0) {
    payload.extraSchedule = extraSchedule;
  }

  return Object.keys(payload).length > 0 ? payload : null;
};

export const buildCategoriesPayload = (
  data: Pick<
    Partial<ProfileTrackingResponse>,
    "categoryId" | "secondaryCategoryIds"
  >,
) => {
  const categoriesIds = [
    data.categoryId,
    ...(data.secondaryCategoryIds || []),
  ].filter((value): value is string => Boolean(value));

  const uniqueCategoryIds = Array.from(new Set(categoriesIds));

  // Sem a principal o backend não sabe qual categoria fica como principal no Google.
  if (!data.categoryId || uniqueCategoryIds.length === 0) return null;

  return { primaryCategoryId: data.categoryId, categoriesIds: uniqueCategoryIds };
};

export const buildLocationPayload = (
  data: Pick<
    Partial<ProfileTrackingResponse>,
    | "serviceAreas"
    | "address"
    | "city"
    | "state"
    | "zipCode"
    | "country"
    | "latitude"
    | "longitude"
  >,
) => {
  const payload: {
    coverageArea?: string[];
    completeAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    lat?: number;
    lng?: number;
  } = {};

  if (data.serviceAreas !== undefined) payload.coverageArea = data.serviceAreas;
  // Campos de endereco: o backend (UpdateBusinessProfileLocation) ja grava todos,
  // mas o frontend nao os enviava — por isso "somiam" ao salvar. Nomes do DTO:
  // completeAddress/city/state/zipCode/country/lat/lng.
  if (data.address !== undefined) payload.completeAddress = data.address;
  if (data.city !== undefined) payload.city = data.city;
  if (data.state !== undefined) payload.state = data.state;
  if (data.zipCode !== undefined) payload.zipCode = data.zipCode;
  if (data.country !== undefined) payload.country = data.country;
  if (data.latitude !== undefined) payload.lat = data.latitude;
  if (data.longitude !== undefined) payload.lng = data.longitude;

  return Object.keys(payload).length > 0 ? payload : null;
};

export const buildContactPayload = (
  data: Pick<
    Partial<ProfileTrackingResponse>,
    "phone" | "email" | "website" | "whatsapp" | "socialMedia"
  >,
) => {
  const payload: {
    mainPhone?: string;
    email?: string;
    website?: string;
    whatsappNumber?: string;
    facebookUrl?: string;
    instagramUrl?: string;
    linkedinUrl?: string;
  } = {};

  if (data.phone !== undefined) payload.mainPhone = data.phone;
  if (data.email !== undefined) payload.email = data.email;
  if (data.website !== undefined) payload.website = data.website;
  if (data.whatsapp !== undefined) payload.whatsappNumber = data.whatsapp;
  if (data.socialMedia?.facebook !== undefined) {
    payload.facebookUrl = data.socialMedia.facebook;
  }
  if (data.socialMedia?.instagram !== undefined) {
    payload.instagramUrl = data.socialMedia.instagram;
  }
  if (data.socialMedia?.linkedin !== undefined) {
    payload.linkedinUrl = data.socialMedia.linkedin;
  }

  return Object.keys(payload).length > 0 ? payload : null;
};

export const buildExtrasPayload = (
  data: Pick<
    Partial<ProfileTrackingResponse>,
    "amenities" | "accessibility" | "parking" | "payments" | "serviceOptions"
  >,
) => {
  const extras: Record<string, boolean> = {};
  const addItems = (items?: string[]) => {
    items?.forEach((item) => {
      if (item) extras[item] = true;
    });
  };

  addItems(data.amenities);
  addItems(data.accessibility);
  addItems(data.parking);
  addItems(data.payments);
  addItems(data.serviceOptions);

  const mappedExtras = Object.fromEntries(
    Object.entries(extras).map(([label, enabled]) => [
      googleAttributeByLabel[label] || label,
      enabled,
    ]),
  );

  return Object.keys(mappedExtras).length > 0 ? { extras: mappedExtras } : null;
};

export type ProfileUpdateBlockResult = {
  block: string;
  endpoint: string;
  ok: boolean;
  message: string;
};

export class ProfileUpdateError extends Error {
  results: ProfileUpdateBlockResult[];

  constructor(results: ProfileUpdateBlockResult[]) {
    const failed = results.filter((result) => !result.ok);
    const succeeded = results.filter((result) => result.ok);
    const firstFailure = failed[0];
    const message =
      succeeded.length > 0
        ? `Algumas alterações foram salvas, mas ${failed.length} bloco(s) falharam. Primeiro erro: ${firstFailure?.message || "erro não identificado"}.`
        : firstFailure?.message || "Não foi possível salvar as alterações.";

    super(message);
    this.name = "ProfileUpdateError";
    this.results = results;
  }
}

export const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    return (
      data?.userMessage ||
      data?.message ||
      data?.error?.message ||
      error.message ||
      fallback
    );
  }

  const responseData = (
    error as {
      response?: {
        data?: {
          userMessage?: string;
          message?: string;
          error?: { message?: string };
        };
      };
    }
  )?.response?.data;

  if (responseData) {
    return (
      responseData.userMessage ||
      responseData.message ||
      responseData.error?.message ||
      fallback
    );
  }

  return error instanceof Error ? error.message : fallback;
};

const modalRequestConfig = (
  tab: string,
  profileId?: string,
): AxiosRequestConfig => ({
  headers: {
    "x-modal-tab": tab,
    ...(profileId ? { "x-profile-public-id": profileId } : {}),
  },
});

export const profilesTrackingService = {
  parseProfileDetails(detailsData: unknown, profileId: string): Partial<ProfileTrackingResponse> {
    if (!detailsData) return { id: profileId };

    const parsed = (
      typeof detailsData === "string" ? JSON.parse(detailsData) : detailsData
      // O /details e um JSON livre e os ~20 campos abaixo sao lidos direto daqui;
      // trocar este `any` por `unknown` quebra o `tsc -b` em 14 pontos.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as Record<string, any>;
    const valueAsString = (value: unknown) =>
      typeof value === "string" ? value : undefined;
    const address =
      parsed.address && typeof parsed.address === "object"
        ? (parsed.address as Record<string, unknown>)
        : {};
    const categories = Array.isArray(parsed.categories)
      ? (parsed.categories as Array<Record<string, unknown>>)
      : [];
    const primaryCategory =
      categories.find((category) => category.primary === true) || categories[0];
    const secondaryCategories = categories.filter(
      (category) => category !== primaryCategory,
    );

    parsed.primaryCategory ||= valueAsString(primaryCategory?.category);
    parsed.categoryId ||= valueAsString(primaryCategory?.gcid);
    parsed.completeAddress ||= valueAsString(address.address);
    parsed.city ||= valueAsString(address.city);
    parsed.state ||= valueAsString(address.state);
    parsed.zipCode ||= valueAsString(address.zipCode);
    parsed.country ||= valueAsString(address.country);
    const valueAsArray = (value: unknown) =>
      Array.isArray(value)
        ? value.map(mapGoogleAttributeToLabel).filter((item): item is string => Boolean(item))
        : undefined;

    return {
      id: profileId,
      name: valueAsString(parsed.displayName) || valueAsString(parsed.name) || "Sem nome",
      category: parsed.primaryCategory || parsed.category || "Não categorizado",
      categoryId: parsed.categoryId,
      status: (parsed.status as ProfileTrackingResponse["status"]) || "preparing",
      shortName: parsed.shortName,
      openingDate: parsed.businessInauguration || parsed.openingDate,
      phone: parsed.mainPhone || parsed.phone,
      email: parsed.email,
      website: parsed.website,
      address: parsed.completeAddress || parsed.address,
      city: parsed.city,
      state: parsed.state,
      zipCode: parsed.zipCode,
      country: parsed.country,
      description: parsed.description,
      secondaryCategories: secondaryCategories
        .map((category) => valueAsString(category.category))
        .filter((value): value is string => Boolean(value)),
      secondaryCategoryIds: secondaryCategories
        .map((category) => valueAsString(category.gcid))
        .filter((value): value is string => Boolean(value)),
      whatsapp: valueAsString(parsed.whatsappNumber) || valueAsString(parsed.whatsapp),
      socialMedia: {
        facebook: valueAsString(parsed.facebookUrl),
        instagram: valueAsString(parsed.instagramUrl),
        linkedin: valueAsString(parsed.linkedinUrl),
      },
      latitude: typeof parsed.lat === "number" ? parsed.lat : undefined,
      longitude: typeof parsed.lng === "number" ? parsed.lng : undefined,
      hours: parsed.hours,
      serviceAreas: Array.isArray(parsed.serviceAreas)
        ? parsed.serviceAreas.filter((item): item is string => typeof item === "string")
        : undefined,
      amenities: valueAsArray(parsed.amenities),
      accessibility: valueAsArray(parsed.accessibility),
      parking: valueAsArray(parsed.parking),
      payments: valueAsArray(parsed.payments),
      serviceOptions: valueAsArray(parsed.serviceOptions),
      // O /details já devolve isto; este mapeamento montava o objeto campo a
      // campo e simplesmente não copiava, então a aba Presença Digital via
      // sempre undefined e dizia "este perfil ainda não tem o link de avaliação
      // — volte após a próxima sincronização". Nenhuma sincronização resolvia,
      // porque o dado já estava lá (Point Car, 03/08).
      reviewUrl: valueAsString(parsed.reviewUrl),
      // Mesma omissão do reviewUrl, e com o mesmo sintoma: o editor do mini-site
      // lê `profile.photos` e mostrava "Galeria: 0 fotos vindas do perfil do
      // Google" mesmo para perfis com fotos gravadas (CSA Casa da Segurança
      // Eletrônica, id 1701: 8 fotos na base de produção). Sem galeria, o
      // "Sugerir capa" caía no acervo genérico e trazia imagem sem relação com o
      // negócio.
      // Não usar valueAsArray aqui: ele passa cada item por
      // mapGoogleAttributeToLabel, que serve a atributos e deturparia as URLs.
      photos: Array.isArray(parsed.photos)
        ? parsed.photos.filter((item): item is string => typeof item === "string")
        : undefined,
    };
  },

  async getProfileBusinessDetails(profileId: string): Promise<ProfileTrackingResponse> {
    try {
      const { user } = useUserStore.getState();

      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };

      const detailsResponse = await ApiService.get(
        `/business-profiles/${profileId}/details`,
        config,
      );
      const detailsData = detailsResponse?.data?.data;
      const details = profilesTrackingService.parseProfileDetails(
        detailsData,
        profileId,
      );

      return {
        id: profileId,
        name: details.name || "Sem nome",
        category: details.category || "Não categorizado",
        categoryId: details.categoryId,
        status: (details.status as ProfileTrackingResponse["status"]) || "preparing",
        stats: {
          views: 0,
          clicks: 0,
          ranking: 0,
        },
        shortName: details.shortName,
        openingDate: details.openingDate,
        phone: details.phone,
        email: details.email,
        website: details.website,
        address: details.address,
        city: details.city,
        state: details.state,
        zipCode: details.zipCode,
        country: details.country,
        description: details.description,
        whatsapp: details.whatsapp,
        socialMedia: details.socialMedia,
        latitude: details.latitude,
        longitude: details.longitude,
        hours: details.hours,
        serviceAreas: details.serviceAreas,
        amenities: details.amenities,
        accessibility: details.accessibility,
        parking: details.parking,
        payments: details.payments,
        serviceOptions: details.serviceOptions,
        secondaryCategories: details.secondaryCategories,
        secondaryCategoryIds: details.secondaryCategoryIds,
        reviewUrl: details.reviewUrl,
        photos: details.photos,
      };
    } catch (error) {
      console.error("Erro ao buscar detalhes do perfil:", error);
      throw error;
    }
  },

  /**
   * Lista todos os perfis de negócio do usuário com suas estatísticas.
   * Recupera a lista de IDs dos profiles do usuário e busca detalhes de cada um.
   * 
   * @returns Array com todos os perfis do usuário com estatísticas
   */
  async listProfiles(): Promise<ProfileTrackingResponse[]> {
    try {
      const { user } = useUserStore.getState();
      const businessProfileIds = user?.businessProfiles || [];

      if (businessProfileIds.length === 0) {
        console.warn("Nenhum business profile encontrado para o usuário");
        return [];
      }

      const profiles: ProfileTrackingResponse[] = [];
      
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };

      for (const profileId of businessProfileIds) {
        try {
          const [detailsResponse, dashboardResponse] = await Promise.all([
            ApiService.get(`/business-profiles/${profileId}/details`, config),
            ApiService.get(`/dashboard/profile/${profileId}?daysToFilter=30`, config),
          ]);

          const detailsData = detailsResponse?.data?.data;
          const details = profilesTrackingService.parseProfileDetails(detailsData, profileId);

          const dashboardData = dashboardResponse?.data?.data || dashboardResponse?.data;

          profiles.push({
            id: profileId,
            name: details.name || "Sem nome",
            category: details.category || "Não categorizado",
            status: (details.status as ProfileTrackingResponse["status"]) || "preparing",
            stats: {
              views: dashboardData?.views || dashboardData?.analytics?.viewsInMap || 0,
              clicks: dashboardData?.clicks || dashboardData?.analytics?.callClicks || 0,
              ranking: dashboardData?.ranking || 0,
            },
            shortName: details.shortName,
            openingDate: details.openingDate,
            phone: details.phone,
            email: details.email,
            website: details.website,
            address: details.address,
            city: details.city,
            state: details.state,
            zipCode: details.zipCode,
            country: details.country,
            description: details.description,
            serviceAreas: details.serviceAreas,
            amenities: details.amenities,
            accessibility: details.accessibility,
            parking: details.parking,
            payments: details.payments,
            serviceOptions: details.serviceOptions,
            // É esta lista que alimenta o `profile` do modal — e é de lá que a
            // aba Presença Digital lê o link do Cartão de Avaliação.
            reviewUrl: details.reviewUrl,
            photos: details.photos,
          });
        } catch (error) {
          console.error(`Erro ao buscar perfil ${profileId}:`, error);
        }
      }
      
      return profiles;
    } catch (error) {
      console.error("Erro ao listar perfis:", error);
      return [];
    }
  },

  /**
   * Recupera detalhes completos de um perfil específico.
   * 
   * @param profileId - Identificador único do perfil
   * @returns Dados completos do perfil solicitado
   */
  async getProfileDetails(profileId: string): Promise<ProfileTrackingResponse> {
    try {
      const { user } = useUserStore.getState();
      
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };
      
      const [detailsResponse, dashboardResponse] = await Promise.all([
        ApiService.get(`/business-profiles/${profileId}/details`, config),
        ApiService.get(`/dashboard/profile/${profileId}?daysToFilter=30`, config),
      ]);

      const detailsData = detailsResponse?.data?.data;
      const details = profilesTrackingService.parseProfileDetails(detailsData, profileId);
      const dashboardData = dashboardResponse?.data?.data || dashboardResponse?.data;

      return {
        id: profileId,
        name: details.name || "Sem nome",
        category: details.category || "Não categorizado",
        status: (details.status as ProfileTrackingResponse["status"]) || "preparing",
        stats: {
          views: dashboardData?.views || dashboardData?.analytics?.viewsInMap || 0,
          clicks: dashboardData?.clicks || dashboardData?.analytics?.callClicks || 0,
          ranking: dashboardData?.ranking || 0,
        },
        shortName: details.shortName,
        openingDate: details.openingDate,
        phone: details.phone,
        email: details.email,
        website: details.website,
        address: details.address,
        city: details.city,
        state: details.state,
        zipCode: details.zipCode,
        country: details.country,
        description: details.description,
        serviceAreas: details.serviceAreas,
        amenities: details.amenities,
        accessibility: details.accessibility,
        parking: details.parking,
        payments: details.payments,
        serviceOptions: details.serviceOptions,
      };
      
      throw new Error("Profile not found");
    } catch (error) {
      console.error("Erro ao buscar detalhes do perfil:", error);
      throw error;
    }
  },

  /**
   * Atualiza dados de um perfil existente.
   * 
   * @param profileId - Identificador único do perfil a atualizar
   * @param data - Dados parciais do perfil a serem atualizados
   */
  async updateProfile(
    profileId: string,
    data: Partial<ProfileTrackingResponse>
  ): Promise<void> {
    try {
      const updatePromises: Promise<ProfileUpdateBlockResult>[] = [];

      const ensureSuccess = (response: unknown) => {
        const responseData = (
          response as {
            data?: { error?: boolean; success?: boolean; message?: string };
          }
        )?.data;

        if (responseData?.error || responseData?.success === false) {
          throw new Error(responseData.message || "Erro ao atualizar perfil");
        }
      };

      const runUpdateBlock = async (
        block: string,
        endpoint: string,
        request: () => Promise<unknown>,
      ): Promise<ProfileUpdateBlockResult> => {
        try {
          const response = await request();
          ensureSuccess(response);
          return {
            block,
            endpoint,
            ok: true,
            message: `${block} salvo com sucesso.`,
          };
        } catch (error) {
          const message = getApiErrorMessage(
            error,
            `Não foi possível salvar ${block}.`,
          );
          console.error("updateProfile block failed", {
            profileId,
            block,
            endpoint,
            message,
          });
          return {
            block,
            endpoint,
            ok: false,
            message,
          };
        }
      };

      // 1. Location
      const locationPayload = buildLocationPayload(data);
      if (locationPayload) {
        const payload = locationPayload;
        updatePromises.push(
          runUpdateBlock(
            "Local e áreas",
            `/business-profiles/${profileId}/location`,
            () =>
              ApiService.put(
                `/business-profiles/${profileId}/location`,
                payload,
                modalRequestConfig("Local e Areas", profileId),
              ),
          ),
        );
      }

      // 2. Schedule
      const schedulePayload = buildSchedulePayload(data);
      if (schedulePayload) {
        const payload = schedulePayload;
        updatePromises.push(
          runUpdateBlock(
            "Horários",
            `/business-profiles/${profileId}/schedule`,
            () =>
              ApiService.put(
                `/business-profiles/${profileId}/schedule`,
                payload,
                modalRequestConfig("Horarios", profileId),
              ),
          ),
        );
      }

      // 3. General
      const generalPayload = buildGeneralPayload(data);
      if (generalPayload) {
        const payload = generalPayload;
        updatePromises.push(
          runUpdateBlock(
            "Informações básicas",
            `/business-profiles/${profileId}/general`,
            () =>
              ApiService.put(
                `/business-profiles/${profileId}/general`,
                payload,
                modalRequestConfig("Informacoes Basicas", profileId),
              ),
          ),
        );
      }

      // 3.1 Categories
      const categoriesPayload = buildCategoriesPayload(data);
      if (categoriesPayload) {
        const payload = categoriesPayload;
        updatePromises.push(
          runUpdateBlock(
            "Categorias",
            `/business-profiles/${profileId}/categories`,
            () =>
              ApiService.post(
                `/business-profiles/${profileId}/categories`,
                payload,
                modalRequestConfig("Informacoes Basicas", profileId),
              ),
          ),
        );
      }

      // 4. Description
      if (data.description !== undefined) {
        const payload = { description: data.description };
        updatePromises.push(
          runUpdateBlock(
            "Descrição",
            `/business-profiles/${profileId}/description`,
            () =>
              ApiService.put(
                `/business-profiles/${profileId}/description`,
                payload,
                modalRequestConfig("Descricao", profileId),
              ),
          ),
        );
      }

      // 5. Contact
      const contactPayload = buildContactPayload(data);
      if (contactPayload) {
        const payload = contactPayload;
        updatePromises.push(
          runUpdateBlock(
            "Contato",
            `/business-profiles/${profileId}/contact`,
            () =>
              ApiService.put(
                `/business-profiles/${profileId}/contact`,
                payload,
                modalRequestConfig("Contato", profileId),
              ),
          ),
        );
      }

      // 6. Extras
      const extrasPayload = buildExtrasPayload(data);
      if (extrasPayload) {
        const payload = extrasPayload;
        updatePromises.push(
          runUpdateBlock(
            "Mais atributos",
            `/business-profiles/${profileId}/extras`,
            () =>
              ApiService.put(
                `/business-profiles/${profileId}/extras`,
                payload,
                modalRequestConfig("Mais Atributos", profileId),
              ),
          ),
        );
      }

      const results = await Promise.all(updatePromises);
      if (results.some((result) => !result.ok)) {
        throw new ProfileUpdateError(results);
      }
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      throw error;
    }
  },

  /**
   * Altera o status operacional de um perfil.
   * Permite ativar, pausar ou colocar em modo pendente.
   * 
   * @param profileId - Identificador único do perfil
   * @param status - Novo status do perfil (active, pending, paused)
   */
  async updateProfileStatus(
    profileId: string,
    status: "active" | "pending" | "paused"
  ): Promise<void> {
    try {
      const { user } = useUserStore.getState();
      const baseUrl = import.meta.env.VITE_APP_URL_BASE;
      
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };
      
      await axios.patch(`${baseUrl}/profiles/${profileId}/status`, { status }, config);
    } catch (error) {
      console.error("Erro ao atualizar status do perfil:", error);
      throw error;
    }
  },

  async updateKeywordRankingRadar(profileId: string): Promise<void> {
    await ApiService.post(`/business-profiles/${profileId}/keywords/ranking/radar`);
  },

  async updateKeywordRankingSerp(profileId: string) {
    const response = await ApiService.post(
      `/business-profiles/${profileId}/keywords/ranking/sync-serp`,
    );
    return response?.data?.data;
  },

  /**
   * Remove um perfil do sistema.
   * Esta operação é irreversível.
   * 
   * @param profileId - Identificador único do perfil a deletar
   */
  async deleteProfile(profileId: string): Promise<void> {
    try {
      const { user } = useUserStore.getState();
      const baseUrl = import.meta.env.VITE_APP_URL_BASE;
      
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };
      
      await axios.delete(`${baseUrl}/profiles/${profileId}`, config);
    } catch (error) {
      console.error("Erro ao deletar perfil:", error);
      throw error;
    }
  },

  async getGabarito(profileId: string) {
    const response = await ApiService.get(
      `/business-profiles/${profileId}/gabarito`,
      modalRequestConfig("Gabarito", profileId),
    );
    return response?.data?.data;
  },

  async saveGabaritoText(
    profileId: string,
    payload: {
      postKeywords: string;
      commentKeywords: string;
      postNotes: string;
      commentNotes: string;
    },
  ) {
    const response = await ApiService.put(
      `/business-profiles/${profileId}/gabarito`,
      payload,
      modalRequestConfig("Gabarito", profileId),
    );
    return response?.data?.data;
  },

  async addGabaritoImage(
    profileId: string,
    payload: { keyword: string; fileName: string; src: string },
  ) {
    const response = await ApiService.post(
      `/business-profiles/${profileId}/gabarito/images`,
      payload,
      modalRequestConfig("Gabarito", profileId),
    );
    return response?.data?.data;
  },

  // Sobe uma imagem de POSTAGEM (depósito próprio, separado do Gabarito) e
  // devolve { id, url } — a URL pública que o Google usa como sourceUrl do post.
  async addPostImage(
    profileId: string,
    payload: { fileName: string; src: string },
  ) {
    const response = await ApiService.post(
      `/business-profiles/${profileId}/post-images`,
      payload,
      modalRequestConfig("Postagem", profileId),
    );
    return response?.data?.data as { id: number; url: string } | undefined;
  },

  async deleteGabaritoImage(profileId: string, imageId: string | number) {
    await ApiService.delete(
      `/business-profiles/${profileId}/gabarito/images/${imageId}`,
    );
  },
};
